import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useWallet } from "@/contexts/WalletContext";
import { toast } from "@/hooks/use-toast";
import {
  searchTokens,
  searchTokensDexScreener,
  getTokenPrice,
  getTrendingTokens,
  getTokenFromDexScreener,
  getBestQuote,
  ExternalToken,
  TokenMetadata,
  SOL_MINT,
} from "@/lib/dex-aggregator";

export interface ImportedToken {
  id: string;
  mint_address: string;
  symbol: string;
  name: string;
  decimals: number;
  logo_uri?: string;
  price: number;
  market_cap: number;
  volume_24h: number;
  liquidity: number;
  price_change_24h: number;
  is_community_locked: boolean;
  lock_votes: number;
  unlock_votes: number;
  imported_at: string;
  imported_by: string;
}

// Combined search result type
export interface SearchResult {
  type: 'platform' | 'external';
  id: string;
  address?: string;
  symbol: string;
  name: string;
  logoURI?: string;
  price?: number;
  marketCap?: number;
  priceChange24h?: number;
}

// Hook for searching and importing external tokens
export function useExternalTokens() {
  const { publicKey } = useWallet();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Search tokens - combines platform coins and external tokens
  const handleSearch = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results: SearchResult[] = [];
      
      // Search platform coins first
      const { data: platformCoins } = await supabase
        .from("coins")
        .select("id, name, ticker, image, price, market_cap, price_change_24h, mint_address")
        .or(`name.ilike.%${query}%,ticker.ilike.%${query}%,mint_address.eq.${query}`)
        .limit(10);
      
      if (platformCoins) {
        for (const coin of platformCoins) {
          results.push({
            type: 'platform',
            id: coin.id,
            address: coin.mint_address || undefined,
            symbol: coin.ticker,
            name: coin.name,
            logoURI: coin.image || undefined,
            price: coin.price || undefined,
            marketCap: coin.market_cap || undefined,
            priceChange24h: coin.price_change_24h || undefined,
          });
        }
      }
      
      // If searching for a Solana address (base58, typically 32-44 chars)
      const isSolanaAddress = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(query);
      
      if (isSolanaAddress) {
        // Search DexScreener for the specific token
        const externalTokens = await searchTokensDexScreener(query);
        for (const token of externalTokens) {
          // Don't add if already in platform results
          if (!results.find(r => r.address === token.mint)) {
            results.push({
              type: 'external',
              id: token.mint,
              address: token.mint,
              symbol: token.symbol,
              name: token.name,
              logoURI: token.logoURI,
              price: token.price,
              marketCap: token.marketCap,
              priceChange24h: token.priceChange24h,
            });
          }
        }
      } else {
        // Search DexScreener for name/symbol matches
        const externalTokens = await searchTokensDexScreener(query);
        for (const token of externalTokens.slice(0, 10)) {
          // Don't add if already in platform results
          if (!results.find(r => r.address === token.mint || r.symbol.toLowerCase() === token.symbol.toLowerCase())) {
            results.push({
              type: 'external',
              id: token.mint,
              address: token.mint,
              symbol: token.symbol,
              name: token.name,
              logoURI: token.logoURI,
              price: token.price,
              marketCap: token.marketCap,
              priceChange24h: token.priceChange24h,
            });
          }
        }
      }
      
      setSearchResults(results);
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  // Get trending tokens from DexScreener
  const { data: trendingTokens = [], isLoading: loadingTrending } = useQuery({
    queryKey: ["trending-tokens"],
    queryFn: getTrendingTokens,
    staleTime: 60000, // 1 minute
    refetchInterval: 60000,
    retry: 2,
  });

  // Import token to our platform
  const importTokenMutation = useMutation({
    mutationFn: async (mint: string) => {
      if (!publicKey) throw new Error("Wallet not connected");

      // Check if already exists in platform
      const { data: existing } = await supabase
        .from("coins")
        .select("id")
        .eq("mint_address", mint)
        .maybeSingle();

      if (existing) {
        return { id: existing.id, alreadyExists: true };
      }

      // Fetch token data from DexScreener
      const tokenData = await getTokenFromDexScreener(mint);
      if (!tokenData) throw new Error("Token not found on any DEX");

      // Create coin record
      const coinId = crypto.randomUUID();
      const { error } = await supabase.from("coins").insert({
        id: coinId,
        mint_address: mint,
        name: tokenData.name,
        ticker: tokenData.symbol,
        description: `Imported from DEX - ${tokenData.name}`,
        image: tokenData.logoURI || null,
        creator: "External Import",
        creator_wallet: publicKey,
        price: tokenData.price,
        market_cap: tokenData.marketCap,
        volume_24h: tokenData.volume24h,
        liquidity: tokenData.liquidity,
        price_change_24h: tokenData.priceChange24h,
        is_locked: false,
        is_premium: false,
        status: "external",
        holders: tokenData.holders || 0,
      });

      if (error) throw error;

      return { id: coinId, alreadyExists: false };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["coins"] });
      queryClient.invalidateQueries({ queryKey: ["discover-platform-coins"] });
      toast({
        title: result.alreadyExists ? "Token Already Listed" : "Token Imported!",
        description: result.alreadyExists
          ? "This token is already available for trading"
          : "Token has been added to the platform",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Import Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    trendingTokens,
    loadingTrending,
    importToken: importTokenMutation.mutateAsync,
    isImporting: importTokenMutation.isPending,
  };
}

// Hook for trading any token via DEX aggregation
export function useUniversalTrading(tokenMint: string) {
  const { connected, publicKey, balance } = useWallet();
  const [amount, setAmount] = useState("");
  const [tradeType, setTradeType] = useState<"buy" | "sell">("buy");
  const [slippage, setSlippage] = useState(0.5);

  // Get current price
  const { data: price } = useQuery({
    queryKey: ["token-price", tokenMint],
    queryFn: () => getTokenPrice(tokenMint),
    enabled: !!tokenMint,
    refetchInterval: 10000,
  });

  // Get quote
  const { data: quote, refetch: refetchQuote } = useQuery({
    queryKey: ["swap-quote", tokenMint, amount, tradeType, slippage],
    queryFn: async () => {
      const parsedAmount = parseFloat(amount);
      if (!parsedAmount || parsedAmount <= 0) return null;

      const inputMint = tradeType === "buy" ? SOL_MINT : tokenMint;
      const outputMint = tradeType === "buy" ? tokenMint : SOL_MINT;
      const lamports = tradeType === "buy"
        ? parsedAmount * 1e9 // SOL to lamports
        : parsedAmount * 1e6; // Assuming 6 decimals for token

      return getBestQuote(inputMint, outputMint, lamports, slippage * 100);
    },
    enabled: !!tokenMint && !!amount && parseFloat(amount) > 0,
    staleTime: 5000,
  });

  // Execute trade (requires wallet signing)
  const executeTrade = useCallback(async () => {
    if (!connected || !publicKey || !quote) {
      throw new Error("Not ready to trade");
    }

    // In production, this would:
    // 1. Get swap transaction from Jupiter
    // 2. Sign with user's wallet
    // 3. Submit to Solana network
    
    toast({
      title: "Trade Submitted",
      description: "Your swap is being processed...",
    });

    return {
      success: true,
      inputAmount: quote.inputAmount,
      outputAmount: quote.outputAmount,
    };
  }, [connected, publicKey, quote]);

  return {
    price,
    amount,
    setAmount,
    tradeType,
    setTradeType,
    slippage,
    setSlippage,
    quote,
    refetchQuote,
    executeTrade,
    canTrade: connected && !!quote && parseFloat(amount) > 0,
  };
}

// Hook for community voting on external tokens
export function useExternalTokenVoting(coinId: string) {
  const { connected, publicKey } = useWallet();
  const queryClient = useQueryClient();

  // Get current vote status
  const { data: voteStatus } = useQuery({
    queryKey: ["external-vote-status", coinId],
    queryFn: async () => {
      const { data } = await supabase
        .from("coins")
        .select("is_locked, votes_to_lock, votes_to_unlock")
        .eq("id", coinId)
        .single();

      return data;
    },
    enabled: !!coinId,
  });

  // Check if user has voted
  const { data: userVote } = useQuery({
    queryKey: ["user-external-vote", coinId, publicKey],
    queryFn: async () => {
      if (!publicKey) return null;

      const { data } = await supabase
        .from("votes")
        .select("vote_direction")
        .eq("coin_id", coinId)
        .eq("voter_wallet", publicKey)
        .eq("vote_type", "external_lock")
        .maybeSingle();

      return data?.vote_direction ?? null;
    },
    enabled: !!coinId && !!publicKey,
  });

  // Cast vote
  const voteMutation = useMutation({
    mutationFn: async (voteToLock: boolean) => {
      if (!publicKey) throw new Error("Wallet not connected");

      // Record vote
      const { error: voteError } = await supabase.from("votes").insert({
        coin_id: coinId,
        voter_wallet: publicKey,
        vote_type: "external_lock",
        vote_direction: !voteToLock, // true = unlock, false = lock
        weight: 1,
      });

      if (voteError) throw voteError;

      // Update coin vote counts
      const field = voteToLock ? "votes_to_lock" : "votes_to_unlock";
      const currentValue = voteToLock
        ? (voteStatus?.votes_to_lock ?? 0)
        : (voteStatus?.votes_to_unlock ?? 0);

      const { error: updateError } = await supabase
        .from("coins")
        .update({ [field]: currentValue + 1 })
        .eq("id", coinId);

      if (updateError) throw updateError;

      // Check if threshold reached (75% to change status)
      const totalVotes = (voteStatus?.votes_to_lock ?? 0) + (voteStatus?.votes_to_unlock ?? 0) + 1;
      const lockPercentage = ((voteStatus?.votes_to_lock ?? 0) + (voteToLock ? 1 : 0)) / totalVotes;
      
      if (lockPercentage >= 0.75 && !voteStatus?.is_locked) {
        await supabase.from("coins").update({ is_locked: true }).eq("id", coinId);
      } else if (lockPercentage < 0.25 && voteStatus?.is_locked) {
        await supabase.from("coins").update({ is_locked: false }).eq("id", coinId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["external-vote-status", coinId] });
      queryClient.invalidateQueries({ queryKey: ["user-external-vote", coinId, publicKey] });
      toast({
        title: "Vote Recorded",
        description: "Your vote has been counted",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Vote Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    isLocked: voteStatus?.is_locked ?? false,
    lockVotes: voteStatus?.votes_to_lock ?? 0,
    unlockVotes: voteStatus?.votes_to_unlock ?? 0,
    userVote,
    canVote: connected && userVote === null,
    castVote: voteMutation.mutateAsync,
    isVoting: voteMutation.isPending,
  };
}
